import React, { useState } from 'react';
import {
  BarChart3,
  AlertTriangle,
  Shield,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  CheckCircle2,
  XCircle
} from 'lucide-react';

interface RiskTheme {
  risk_type: string;
  total_instances: number;
  steps_affected: number;
  severity_indicator: 'high' | 'medium' | 'low';
  critical_steps: string[];
  summary_text: string;
}

interface RiskConcentration {
  high_density_steps: any[];
  step_names: string[];
  percentage_of_total: number;
  insight_text: string;
}

interface ControlGaps {
  steps_without_controls: string[];
  total_count: number;
  high_severity_gaps: number;
  insight_text: string;
}

interface RiskSummary {
  metrics: {
    unique_risk_types: number;
    total_risk_instances: number;
    high_priority_count: number;
    controls_mapped: number;
    control_gap_count: number;
    steps_with_gaps: number;
    control_coverage_pct: number;
  };
  themes: RiskTheme[];
  concentration: RiskConcentration | null;
  gaps: ControlGaps | null;
  insights: string[];
}

interface RiskSummaryPanelProps {
  summary: RiskSummary | null;
  loading?: boolean;
}

export const RiskSummaryPanel: React.FC<RiskSummaryPanelProps> = ({ summary, loading }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl shadow-sm border border-blue-200 p-6 mb-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Analyzing risk landscape...</span>
        </div>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const { metrics, themes, insights } = summary;

  const getSeverityColor = (indicator: string) => {
    switch (indicator) {
      case 'high': return 'bg-red-100 text-red-700 border-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-700 border-green-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getControlCoverageColor = (pct: number) => {
    if (pct >= 80) return 'text-green-600';
    if (pct >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl shadow-sm border border-blue-200 mb-6 overflow-hidden">
      {/* Header */}
      <div
        className="bg-white bg-opacity-80 backdrop-blur-sm p-4 flex items-center justify-between cursor-pointer hover:bg-opacity-100 transition-all"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-600 rounded-lg">
            <BarChart3 className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Risk Assessment Summary</h2>
            <p className="text-sm text-gray-600">High-level insights and key metrics</p>
          </div>
        </div>
        <button
          className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-600" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-600" />
          )}
        </button>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-6 space-y-6">
          {/* Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Risk Types */}
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Risk Types</span>
              </div>
              <div className="text-2xl font-bold text-blue-600">
                {metrics.unique_risk_types}
              </div>
              <div className="text-xs text-gray-500 mt-1">Unique categories</div>
            </div>

            {/* Total Instances */}
            <div className="bg-white rounded-lg p-4 border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-medium text-gray-600">Total Risks</span>
              </div>
              <div className="text-2xl font-bold text-purple-600">
                {metrics.total_risk_instances}
              </div>
              <div className="text-xs text-gray-500 mt-1">Identified instances</div>
            </div>

            {/* High Priority */}
            <div className="bg-white rounded-lg p-4 border border-red-200">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <span className="text-sm font-medium text-gray-600">High Priority</span>
              </div>
              <div className="text-2xl font-bold text-red-600">
                {metrics.high_priority_count}
              </div>
              <div className="text-xs text-gray-500 mt-1">Require immediate attention</div>
            </div>

            {/* Control Coverage */}
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-gray-600">Control Coverage</span>
              </div>
              <div className={`text-2xl font-bold ${getControlCoverageColor(metrics.control_coverage_pct)}`}>
                {metrics.control_coverage_pct}%
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {metrics.controls_mapped}/{metrics.total_risk_instances} mapped
              </div>
            </div>
          </div>

          {/* Risk Themes */}
          {themes && themes.length > 0 && (
            <div className="bg-white rounded-lg p-5 border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                <h3 className="text-base font-semibold text-gray-900">Primary Risk Themes</h3>
              </div>
              <div className="space-y-3">
                {themes.map((theme, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="flex-shrink-0">
                        <div className={`px-3 py-1 rounded-full text-xs font-bold border ${getSeverityColor(theme.severity_indicator)}`}>
                          {theme.severity_indicator.toUpperCase()}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{theme.risk_type}</div>
                        <div className="text-sm text-gray-600">
                          {theme.summary_text.replace(theme.risk_type, '').replace(/^\s*\(/, '').replace(/\)\s*$/, '')}
                        </div>
                        {theme.critical_steps && theme.critical_steps.length > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            Critical: {theme.critical_steps.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Insights */}
          {insights && insights.length > 0 && (
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-5 border border-purple-200">
              <div className="flex items-center gap-2 mb-4">
                <Lightbulb className="h-5 w-5 text-purple-600" />
                <h3 className="text-base font-semibold text-gray-900">Key Insights</h3>
              </div>
              <div className="space-y-2">
                {insights.map((insight, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {insight.toLowerCase().includes('no controls') ||
                      insight.toLowerCase().includes('gap') ? (
                        <XCircle className="h-4 w-4 text-red-500" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4 text-blue-500" />
                      )}
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">{insight}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer Note */}
          <div className="text-center text-xs text-gray-500 pt-2 border-t border-gray-200">
            💡 This summary provides a high-level view. Scroll down for detailed step-by-step risk assessments.
          </div>
        </div>
      )}
    </div>
  );
};
